import { useEffect } from 'react'
import SubHeaders from '@/components/Headers/SubHeaders'
import useTabs from '@/hooks/useTabs'
import AddIcons from '@/icons/AddIcons'
import { paths } from '@/utils/path'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Tabs } from 'vnetwork-ui'
import { tabs } from './tab'
import { useModal } from '@/contexts/modal'
import { useTourHandler } from '@/contexts/tour-handler'
import { useEmployeeListTour } from '../../hooks/use-employee-list-tour'
import { useDepartmentListTour } from '../../hooks/use-department-list-tour'
import { useEmployeeListQuickHelp } from '../../hooks/use-employee-list-quick-help'

const List = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { setModal } = useModal()
    const [searchParams] = useSearchParams()

    const activeTab = searchParams.get('tab') || 'employees'

    // Tour hooks
    const { startTour: startEmployeeTour } = useEmployeeListTour()
    const { startTour: startDepartmentTour } = useDepartmentListTour()
    const { registerHandler, unregisterHandler } = useTourHandler()

    // Quick help hook
    useEmployeeListQuickHelp()

    // Register tour handler based on active tab
    useEffect(() => {
        const handler =
            activeTab === 'departments'
                ? startDepartmentTour
                : startEmployeeTour
        registerHandler(handler)
        return () => unregisterHandler()
    }, [
        activeTab,
        registerHandler,
        unregisterHandler,
        startEmployeeTour,
        startDepartmentTour,
    ])

    const { TabComponent, TabProps } = useTabs({
        tabs: tabs(),
        clearFilters: true,
    })

    // Dynamic actions based on active tab
    const getActions = () => {
        if (activeTab === 'departments') {
            return [
                {
                    icon: <AddIcons />,
                    label: t('create'),
                    onClick: () =>
                        setModal('DepartmentModals/Create/index', {
                            refetch: () => window.location.reload(),
                        }),
                    variant: 'contained' as const,
                    'data-tour': 'create-department-btn',
                },
            ]
        }
        return [
            {
                icon: <AddIcons />,
                label: t('add-employee'),
                onClick: () => navigate(paths.employeeCreate),
                variant: 'contained' as const,
                'data-tour': 'create-employee-btn',
            },
        ]
    }

    return (
        <div>
            <SubHeaders
                actions={getActions()}
                breadcrumbs={[
                    {
                        path: paths.employeesManage,
                        title: t('employee-manage'),
                    },
                ]}
            />

            <div data-tour="employee-list" className="space-y-2">
                <div data-tour="employee-tabs">
                    <Tabs {...TabProps} />
                </div>
                {TabComponent}
            </div>
        </div>
    )
}

export default List
